import { useState, useMemo } from "react";
import { CLARIFICATION_STATUS_LABELS, CLARIFICATION_IMPACT_LABELS } from "../../../constants/clarification.constants";
import type { ClarificationStatus, ClarificationImpact, Clarification } from "../../../types/clarification.type";

interface UseFilterStateOptions {
  suppliers: Array<{ id: number; name: string }>;
  displayClarifications: Clarification[];
  supplierFilter?: number;
}

export const useFilterState = ({ suppliers, displayClarifications, supplierFilter }: UseFilterStateOptions) => {
  const [supplierFilters, setSupplierFilters] = useState<number[]>([]);
  const [statusFilters, setStatusFilters] = useState<ClarificationStatus[]>([]);
  const [impactFilters, setImpactFilters] = useState<(ClarificationImpact | "not_set")[]>([]);

  const filteredClarifications = useMemo(() => {
    let filtered = displayClarifications;

    if (supplierFilters.length > 0) {
      filtered = filtered.filter((c) => supplierFilters.includes(c.rfx_submission_id));
    }

    if (statusFilters.length > 0) {
      filtered = filtered.filter((c) => statusFilters.includes(c.status));
    }

    if (impactFilters.length > 0) {
      filtered = filtered.filter((c) => {
        if (impactFilters.includes("not_set") && !c.clarification_impact_id) {
          return true;
        }
        return c.clarification_impact_id && impactFilters.includes(c.clarification_impact_id);
      });
    }

    return filtered;
  }, [displayClarifications, supplierFilters, statusFilters, impactFilters]);

  const activeFilterCount = supplierFilters.length + statusFilters.length + impactFilters.length;

  const activeFilterDetails = useMemo(() => {
    const details: Array<{ label: string; type: "supplier" | "status" | "impact"; value: any }> = [];

    supplierFilters.forEach((id) => {
      const supplier = suppliers.find((s) => s.id === id);
      if (supplier) {
        details.push({ type: "supplier", label: `Supplier: ${supplier.name}`, value: id });
      }
    });

    statusFilters.forEach((status) => {
      details.push({ type: "status", label: `Status: ${CLARIFICATION_STATUS_LABELS[status] || status}`, value: status });
    });

    impactFilters.forEach((impact) => {
      const impactLabel = impact === "not_set" ? "Not Set" : CLARIFICATION_IMPACT_LABELS[impact];
      details.push({ type: "impact", label: `Impact: ${impactLabel}`, value: impact });
    });

    return details;
  }, [supplierFilters, statusFilters, impactFilters, suppliers]);

  const handleClearAllFilters = () => {
    setSupplierFilters([]);
    setStatusFilters([]);
    setImpactFilters([]);
  };

  const handleRemoveSupplierFilter = (supplierId: number) => {
    setSupplierFilters((prev) => prev.filter((id) => id !== supplierId));
  };

  const handleRemoveStatusFilter = (status: ClarificationStatus) => {
    setStatusFilters((prev) => prev.filter((s) => s !== status));
  };

  const handleRemoveImpactFilter = (impact: ClarificationImpact | "not_set") => {
    setImpactFilters((prev) => prev.filter((i) => i !== impact));
  };

  return {
    supplierFilters,
    setSupplierFilters,
    statusFilters,
    setStatusFilters,
    impactFilters,
    setImpactFilters,
    filteredClarifications,
    activeFilterCount,
    activeFilterDetails,
    handleClearAllFilters,
    handleRemoveSupplierFilter,
    handleRemoveStatusFilter,
    handleRemoveImpactFilter,
  };
};

export type FilterStateReturn = ReturnType<typeof useFilterState>;
